import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Cookies from 'js-cookie';
import React, {Suspense, useEffect, useRef} from 'react';
import {Link, Navigate, Route, Routes, useLocation} from 'react-router-dom';
import InstructorAccessGuard from 'Components/InstructorAccessGuard';
import {
  APP_BAR_HEIGHT,
  DRAWER_WIDTH,
  FIXED_HEADER_HEIGHT_VAR,
} from 'Components/SideBar/drawerWidths';
import EmailTemplateComposer from 'Screens/EmailTemplateComposer/EmailTemplateComposer';
import EmptyPage from 'Screens/Lost/EmptyPage';
import {
  CourseManagementMotionProvider,
  CourseManagementRouteEntry,
} from 'Screens/TrainingManagement/courseManagementMotion';
import {courseManagementRouteLoaders} from 'Screens/TrainingManagement/courseManagementRouteLoaders';
import packageJson from '../package.json';
import {logout} from './Actions/AuthActions';
import {ACCESS_TOKEN, ACCESS_TOKEN_EXPIRY, getAccessToken} from './Api';
import {APP_COUNTRY} from './DataConstants';

const LAZY_COMPONENTS = {
  Login: React.lazy(() => import('Screens/Login/Login')),
  PasswordExpiryBanner: React.lazy(() => import('Components/PasswordExpiryBanner')),
  RedirectComponent: React.lazy(() => import('Screens/Redirect/Redirect')),
  RootDirectory: React.lazy(() => import('Screens/RootDirectory')),
  AccountDetails: React.lazy(() => import('Screens/AccountDetails')),
  AdminSideBar: React.lazy(() => import('Components/SideBar/AdminSideBar')),
  AdminSideBarExpander: React.lazy(() => import('Components/SideBar/AdminSideBarExpander')),
  B2BExportPage: React.lazy(() => import('Screens/B2BExportPage')),
  DesignSystemCatalog: React.lazy(() => import('design-system/catalog')),
  CpCheckInLab: React.lazy(() => import('Screens/CpCheckIn/lab')),
  TrainingAssessmentLab: React.lazy(() => import('Screens/TrainingAssessmentLab')),
  PracticalAssessmentLab: React.lazy(() => import('design-system/patterns/training-assessment/lab')),
  CareOwnerIndex: React.lazy(() => import('Screens/CareOwner/CareOwnerIndex')),
  CPDashboard: React.lazy(() => import('Screens/CPDashboard/CPDashboard')),
  CPVisits: React.lazy(() => import('Screens/CPDashboard/CPVisits/index')),
  CPEngagementTracker: React.lazy(() => import('Screens/CPEngagementTracker')),
  CPGSTStatus: React.lazy(() => import('Screens/CPGSTStatus')),
  CPWaivers: React.lazy(() => import('Screens/CPWaivers')),
  ManageCareRecipient: React.lazy(() => import('Screens/ManageCareRecipientB2C/ManageCareRecipient')),
  CombinedCPRosterCalendarView: React.lazy(() => import('Screens/CPRoster/CombinedCPRosterCalendarView')),
  OfflinePaymentSettings: React.lazy(() => import('Screens/OfflinePaymentSettings')),
  PaymentsDashboard: React.lazy(() => import('Screens/PaymentsDashboard')),
  PCAppActivationDashboard: React.lazy(() => import('Screens/PCAppActivationDashboard')),
  PricingEngine: React.lazy(() => import('Screens/Pricing/PricingEngineIndex')),
  PriorityInbox: React.lazy(() => import('Screens/PriorityInbox')),
  RecruitmentWorkflowList: React.lazy(() => import('Screens/RecruitmentWorkflowList/RecruitmentWorkflowList')),
  RecruitmentWorkflowSettings: React.lazy(
    () => import('Screens/RecruitmentWorkflowSettings/RecruitmentWorkflowSettings'),
  ),
  STCJobs: React.lazy(() => import('Screens/ShortTermContract/STCJobs')),
  Visits: React.lazy(() => import('Screens/Visits')),
  ServiceCoordinatorOperator: React.lazy(() => import('Screens/ServiceCoordinatorOperator')),
  CP2MActionCenter: React.lazy(() => import('Screens/CP2MActionCenter')),
  StrikeDetail: React.lazy(() => import('Screens/CP2MActionCenter/StrikeDetail')),
  NotificationDashboard: React.lazy(() => import('Screens/NotificationDashboard')),
  TrainingManagement: React.lazy(courseManagementRouteLoaders.overview),
  CreateTrainingSchedule: React.lazy(courseManagementRouteLoaders.createSchedule),
  InstructorManagement: React.lazy(courseManagementRouteLoaders.instructors),
  EventDetails: React.lazy(courseManagementRouteLoaders.eventDetails),
  CpSessionDetails: React.lazy(courseManagementRouteLoaders.cpSessionDetails),
  CpCheckIn: React.lazy(() => import('Screens/CpCheckIn')),
  TheoryAssessment: React.lazy(() => import('Screens/TheoryAssessment')),
  CareProSignupForm: React.lazy(() => import('./External Pages/CareProSignupForm')),
  PaymentWebLink: React.lazy(() => import('./External Pages/PaymentWebLink')),
  PIQForm: React.lazy(() => import('./External Pages/PIQForm')),
  CareProProfile: React.lazy(() => import('./External Pages/CareProProfile')),
} as const;

const RouteLoadingFallback = () => <div className="route-loading" role="status" aria-label="Loading page" />;
const SidebarLoadingFallback = () => (
  <div
    className="sidebar-loading"
    role="status"
    aria-label="Loading navigation"
    style={{width: DRAWER_WIDTH, flexShrink: 0}}
  />
);

const DeprecatedEmailDashboardRedirect = () => {
  const location = useLocation();

  return (
    <Navigate
      replace
      to={{pathname: '/notification-dashboard', search: location.search, hash: location.hash}}
    />
  );
};

const appBarToolbarSx = {
  minHeight: `${APP_BAR_HEIGHT} !important`,
  height: APP_BAR_HEIGHT,
  px: '1.25rem',
};

const headerSpacerSx = {
  minHeight: `var(${FIXED_HEADER_HEIGHT_VAR}, ${APP_BAR_HEIGHT}) !important`,
  height: `var(${FIXED_HEADER_HEIGHT_VAR}, ${APP_BAR_HEIGHT})`,
  flexShrink: 0,
};

const logOut = async () => {
  if (await logout()) {
    Cookies.remove('userid');
    Cookies.remove(ACCESS_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY);
    window.location.reload();
  }
};

const getEnvironment = (apiUrl = process.env.REACT_APP_API_URL || '') => {
  if (apiUrl.includes('dev')) return 'dev';
  if (apiUrl.includes('testing')) return 'staging';
  return '';
};

/** UI labs (check-in fixtures, etc.) — local + staging/dev APIs only, never production. */
export const isNonProductionUiLabEnabled = (
  nodeEnvironment = process.env.NODE_ENV,
  apiUrl = process.env.REACT_APP_API_URL || '',
  region = process.env.REACT_APP_REGION || '',
) => nodeEnvironment === 'development' || region.endsWith('_LOCAL') || Boolean(getEnvironment(apiUrl));

const displayVersionAndCountryAndEnvironment = () => {
  const envString = getEnvironment();
  return envString ? ` ${packageJson.version}-${envString}-${APP_COUNTRY}` : '';
};

const NavBar = () => (
  <div className="navigation-bar h center">
    <img className="homage-logo" src="/homage_logo_white.png" alt="" onClick={logOut} />
    <span className="homage-title">HOMAGE{displayVersionAndCountryAndEnvironment()}</span>
  </div>
);

const AdminNavBar = () => (
  <AppBar
    position="relative"
    elevation={0}
    sx={{
      // Clipped drawer: header stack z-index is above Drawer; bar spans full width.
      backgroundColor: '#32bdc7',
      borderBottom: '1px solid #32bdc7',
      color: '#fff',
    }}
  >
    <Toolbar disableGutters sx={{...appBarToolbarSx, display: 'flex', alignItems: 'center'}}>
      <Suspense fallback={<div />}>
        <LAZY_COMPONENTS.AdminSideBarExpander />
      </Suspense>
      <Link className="h vc" to="/" style={{color: 'inherit', textDecoration: 'none'}}>
        <img className="homage-logo" src="/homage_logo_white.png" alt="" />
        <span className="homage-title">HOMAGE{displayVersionAndCountryAndEnvironment()}</span>
      </Link>
      <div className="logout" onClick={logOut}>
        Logout
      </div>
    </Toolbar>
  </AppBar>
);

const CourseManagementRoutes = () => (
  <CourseManagementMotionProvider>
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route
          path="schedules/new"
          element={
            <CourseManagementRouteEntry><LAZY_COMPONENTS.CreateTrainingSchedule /></CourseManagementRouteEntry>
          }
        />
        <Route
          path="instructors"
          element={
            <CourseManagementRouteEntry><LAZY_COMPONENTS.InstructorManagement /></CourseManagementRouteEntry>
          }
        />
        <Route
          path=":eventId/care-pros/:cpId"
          element={
            <CourseManagementRouteEntry><LAZY_COMPONENTS.CpSessionDetails /></CourseManagementRouteEntry>
          }
        />
        <Route
          path=":eventId"
          element={
            <CourseManagementRouteEntry><LAZY_COMPONENTS.EventDetails /></CourseManagementRouteEntry>
          }
        />
        <Route
          index
          element={
            <CourseManagementRouteEntry><LAZY_COMPONENTS.TrainingManagement /></CourseManagementRouteEntry>
          }
        />
      </Routes>
    </Suspense>
  </CourseManagementMotionProvider>
);

const PrivateContentRoutes = () => (
  <Routes>
    <Route path="/" element={<LAZY_COMPONENTS.RootDirectory />} />
    <Route path="/account-details" element={<LAZY_COMPONENTS.AccountDetails />} />
    <Route path="/careowner" element={<LAZY_COMPONENTS.CareOwnerIndex />} />
    <Route path="/careowner/:id/:cr_id?" element={<LAZY_COMPONENTS.CareOwnerIndex />} />
    <Route path="/cr/:id/:co_id?/:service_type?" element={<LAZY_COMPONENTS.ManageCareRecipient />} />
    <Route path="/offline-payment-settings" element={<LAZY_COMPONENTS.OfflinePaymentSettings />} />
    <Route path="/cp-waivers" element={<LAZY_COMPONENTS.CPWaivers />} />
    <Route path="/cp-gst-status" element={<LAZY_COMPONENTS.CPGSTStatus />} />
    <Route path="/stc" element={<LAZY_COMPONENTS.STCJobs />} />
    <Route path="/pricing" element={<LAZY_COMPONENTS.PricingEngine />} />
    <Route path="/priority-inbox" element={<LAZY_COMPONENTS.PriorityInbox />} />
    <Route path="/payments-dashboard" element={<LAZY_COMPONENTS.PaymentsDashboard />} />
    <Route path="/b2b/nok-activation-dashboard" element={<LAZY_COMPONENTS.PCAppActivationDashboard />} />
    <Route path="/recruitment-workflow-list" element={<LAZY_COMPONENTS.RecruitmentWorkflowList />} />
    <Route path="/recruitment-workflow-settings" element={<LAZY_COMPONENTS.RecruitmentWorkflowSettings />} />
    <Route path="/b2b/*" element={<LAZY_COMPONENTS.B2BExportPage />} />
    <Route path="/individual_visits" element={<LAZY_COMPONENTS.Visits />} />
    <Route path="/cp-dashboard" element={<LAZY_COMPONENTS.CPDashboard />} />
    <Route path="/cp-engagement-tracker" element={<LAZY_COMPONENTS.CPEngagementTracker />} />
    <Route path="/email-dashboard" element={<DeprecatedEmailDashboardRedirect />} />
    <Route path="/notification-dashboard" element={<LAZY_COMPONENTS.NotificationDashboard />} />
    <Route path="/course-management/*" element={<CourseManagementRoutes />} />
    <Route path="/sco-management" element={<LAZY_COMPONENTS.ServiceCoordinatorOperator />} />
    <Route path="/cp2m_action_center" element={<LAZY_COMPONENTS.CP2MActionCenter />} />
    <Route path="/strike/:id" element={<LAZY_COMPONENTS.StrikeDetail />} />
    <Route path="/standby_cp_roster" element={<LAZY_COMPONENTS.CombinedCPRosterCalendarView />} />
    <Route path="/cp-roster" element={<LAZY_COMPONENTS.CPVisits />} />
    <Route path="/cp-roster/:id" element={<LAZY_COMPONENTS.CPVisits />} />
    <Route path="/compose-email-template" element={<EmailTemplateComposer />} />
    <Route path="*" element={<Navigate to="/login" replace />} />
  </Routes>
);

export const ProtectedLayout = React.memo(() => {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) {
      return undefined;
    }

    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        FIXED_HEADER_HEIGHT_VAR,
        `${headerEl.offsetHeight}px`,
      );
    };

    syncHeaderHeight();

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        document.documentElement.style.removeProperty(FIXED_HEADER_HEIGHT_VAR);
      };
    }

    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(headerEl);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(FIXED_HEADER_HEIGHT_VAR);
    };
  }, []);

  return (
    <>
      {/* Fixed header stack: banner (when present) + clipped AppBar above the drawer */}
      <Box
        ref={headerRef}
        className="admin-fixed-header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // Stay above collapsed drawer flyouts (drawer + 2) so the hamburger stays clickable
          zIndex: (theme) => theme.zIndex.drawer + 3,
        }}
      >
        <Suspense fallback={null}>
          <LAZY_COMPONENTS.PasswordExpiryBanner />
        </Suspense>
        <AdminNavBar />
      </Box>
      <Box className="app-section" sx={{display: 'flex', width: '100%', boxSizing: 'border-box'}}>
        <Suspense fallback={<SidebarLoadingFallback />}>
          <LAZY_COMPONENTS.AdminSideBar />
        </Suspense>
        <Box
          component="main"
          id="right"
          className="content-section internal-page-canvas"
          sx={{flexGrow: 1, width: 'auto', minWidth: 0}}
        >
          {/* Spacer clears fixed header (banner + AppBar) */}
          <Toolbar disableGutters className="admin-main-toolbar-spacer" sx={headerSpacerSx} />
          <div className="main-container">
            <Suspense fallback={<RouteLoadingFallback />}>
              <InstructorAccessGuard loadingFallback={<RouteLoadingFallback />}>
                <PrivateContentRoutes />
              </InstructorAccessGuard>
            </Suspense>
          </div>
        </Box>
      </Box>
    </>
  );
});

export const AuthenticatedApp = () => {
  const location = useLocation();
  return getAccessToken()
    ? <ProtectedLayout />
    : <Navigate to="/login" state={{from: location}} replace />;
};

const paymentWebLinkElement = (
  <Suspense fallback={<div />}><LAZY_COMPONENTS.PaymentWebLink /></Suspense>
);

const AppRoutes = () => (
  <Routes>
    {process.env.NODE_ENV === 'development' && (
      <Route
        path="/__design-system"
        element={<Suspense fallback={<div />}><LAZY_COMPONENTS.DesignSystemCatalog /></Suspense>}
      />
    )}
    {isNonProductionUiLabEnabled() && (
      <Route
        path="/__cp-check-in"
        element={<Suspense fallback={<div />}><LAZY_COMPONENTS.CpCheckInLab /></Suspense>}
      />
    )}
    {isNonProductionUiLabEnabled() && (
      <Route
        path="/__training-assessment"
        element={<Suspense fallback={<div />}><LAZY_COMPONENTS.TrainingAssessmentLab /></Suspense>}
      />
    )}
    {isNonProductionUiLabEnabled() && (
      <Route
        path="/__practical-assessment"
        element={<Suspense fallback={<div />}><LAZY_COMPONENTS.PracticalAssessmentLab /></Suspense>}
      />
    )}
    <Route
      path="/piq/apply"
      element={<Suspense fallback={<div />}><LAZY_COMPONENTS.PIQForm /></Suspense>}
    />
    <Route
      path="/public-carepro-profile"
      element={<Suspense fallback={<div />}><LAZY_COMPONENTS.CareProProfile /></Suspense>}
    />
    {!!(process.env.REACT_APP_CP_APPLY || !process.env.REACT_APP_DISABLE_VAL) && (
      process.env.REACT_APP_DISABLE_VAL ? (
        <Route
          path="*"
          element={<Suspense fallback={<div />}><LAZY_COMPONENTS.CareProSignupForm /></Suspense>}
        />
      ) : (
        <Route
          path="/carepro/apply"
          element={<Suspense fallback={<div />}><LAZY_COMPONENTS.CareProSignupForm /></Suspense>}
        />
      )
    )}
    {!!(process.env.REACT_APP_PAYMENTS_DOMAIN || !process.env.REACT_APP_DISABLE_VAL) && (
      process.env.REACT_APP_PAYMENTS_DOMAIN ? (
        <>
          <Route path="/:paymentId" element={paymentWebLinkElement} />
          <Route path="/:paymentId/*" element={paymentWebLinkElement} />
        </>
      ) : (
        <>
          <Route path="/weblink/:paymentId" element={paymentWebLinkElement} />
          <Route path="/weblink/:paymentId/*" element={paymentWebLinkElement} />
        </>
      )
    )}
    <Route path="/lost" element={<><NavBar /><EmptyPage /></>} />
    <Route
      path="/login"
      element={
        <>
          <NavBar />
          <Suspense fallback={<div />}><LAZY_COMPONENTS.Login /></Suspense>
        </>
      }
    />
    <Route
      path="/redirect"
      element={
        <>
          <NavBar />
          <Suspense fallback={<div />}><LAZY_COMPONENTS.RedirectComponent /></Suspense>
        </>
      }
    />
    <Route path="/cp-dashboard/care-pro-tracker" element={<Navigate to="/cp-engagement-tracker" replace />} />
    <Route
      path="/check-in/:checkInId/theory"
      element={<Suspense fallback={<div />}><LAZY_COMPONENTS.TheoryAssessment /></Suspense>}
    />
    <Route
      path="/check-in/:checkInId"
      element={<Suspense fallback={<div />}><LAZY_COMPONENTS.CpCheckIn /></Suspense>}
    />
    <Route path="/*" element={<AuthenticatedApp />} />
  </Routes>
);

export default AppRoutes;
